

import { Component, Vue, Prop, Emit } from "vue-property-decorator"
import { Getter, Mutation, Action, State } from "vuex-class"

import { LoginResultTenant } from "@/types"
import User, { CurrentUser, SystemUser } from "@/types/user"

import api from "@/services/api"
import tenantApi from "@/services/api_Tenant"
import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import LightDarkSwitch from "./Account/LightDarkSwitch.vue"
import UserSettingsModal from "@/components/UserSettingsModal.vue"
import { Subscription } from "@/types/subscription"
import { TenantSupport } from "@/types/tenant"
import SetStatusModal from "@/components/SetStatusModal.vue"
import TenantInfoDisplay from "@/components/Info/TenantInfoDisplay.vue"

import Guid from "@/scripts/guid"
import NewInterfaceSwitch from "@/components/Account/NewInterfaceSwitch.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import BaseNotificationMenu from "@/components/Notifications/BaseNotificationsMenu.vue"

@Component({
	components: {
		"new-interface-switch": NewInterfaceSwitch,
		"v-select": vSelect,
		"light-dark": LightDarkSwitch,
		"scrollbar": VuePerfectScrollbar,
		"user-settings-modal": UserSettingsModal,
		"set-status-modal": SetStatusModal,
		"info-display": TenantInfoDisplay,
		"notification-menu": BaseNotificationMenu
	}
})
export default class NavHeader extends Vue {
	$refs!: {
		userSettingsModal: UserSettingsModal,
		setStatusModal: SetStatusModal
	}

	@Prop(String)
	title!: string;

	@Getter("getScreenWidth") public screenWidth!: number;

	@State public systemUser!: SystemUser
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getSubscription") subscription?: Subscription
	@Getter("hasSubscription") hasSubscription!: boolean
	@Getter("getRecordName") getRecordName!: string;

	@Mutation("setSearch") setSearch!: (search: string) => void;

	@Mutation
	private setManualLogout!: (manualLogout: boolean) => void

	@Action setTheme!: (theme: string) => void;

	@Action("selectTenant")
	public loginSelectTenant!: (tenant: LoginResultTenant) => Promise<void>

	@Action("logOut")
	public logOut!: () => void;

	public search!: string;
	public searchResults: any[] = [];
	public selectedItem: any = null;

	private pictureError: boolean = false;
	private pictureGuid: string = Guid.newGuid();

	private debounceSearch: NodeJS.Timeout | null = null;

	infoShown: boolean = false

	supportAccess: TenantSupport | null = null
	supportAccessOpen: boolean = false
	supportAccessTime: { label: string, value: string | null } | null = null
	supportTimes = [
		{
			label: "1 hour",
			value: "01:00:00"
		},
		{
			label: "4 hours",
			value: "4:00:00"
		},
		{
			label: "12 hours",
			value: "12:00:00"
		},
		{
			label: "1 day",
			value: "1.00:00:00"
		},
		{
			label: "1 week",
			value: "7.00:00:00"
		}
	]

	public get viewSearchResults (): any[] {
		return this.searchResults
	}

	public get myPictureUrl (): string {
		let imageUrl = ""

		if (this.pictureError || (!this.me && !this.systemUser)) {
			imageUrl = "images/blank_user.png"
		} else {
			if (this.systemUser) {
				imageUrl = this.systemUser.pictureUrl ? this.systemUser.pictureUrl : "images/blank_user.png"
			} else {
				if (this.me.pictureUrl) {
					imageUrl = this.me.pictureUrl!
				} else {
					imageUrl = api.pictureUrl(this.me.id)
				}
			}
		}

		return `${imageUrl}?p=${this.pictureGuid}`
	}

	private get otherTenants (): LoginResultTenant[] {
		if (!this.me || !this.me.loginTenants) {
			return []
		}

		return this.me.loginTenants.filter(t => !t.disabled && t.id !== this.me.tenantId)
	}

	get infoEnabled (): boolean {
		return this.me && !!this.me.hasTenantInfo && this.tenantEnabledFeatures["tenantInfo"]
	}

	/**
	 * Only show new interface switch if the features are enabled
	 * @private
	 */
	private get showNewInterfaceSwitch () {
		return (this.me?.tenantEnabledFeatures?.includes(TenantFeatures.EnableV2) && (this.me?.isAdmin || this.me?.tenantEnabledFeatures?.includes(TenantFeatures.NewUIOptIn)))
	}

	public mounted (): void {
		this.$root.$on("refresh-profile-picture", this.refreshProfilePicture)
	}

	public beforeDestroy (): void {
		this.$root.$off("refresh-profile-picture", this.refreshProfilePicture)
	}

	public async logout (): Promise<void> {
		this.setManualLogout(true)

		await api.logout()
		this.logOut()
	}

	public toMyAccount (): void {
		this.$router.push({ path: `/userprofile/${this.me.id}` })
	}

	public showUserSettings (): void {
		this.$refs.userSettingsModal.open()
	}

	public showSetStatus (): void {
		this.$refs.setStatusModal.open()
	}

	public toOrganization (): void {
		this.$router.push({ path: "/organization" })
	}

	@Emit("menuOpen")
	public menuSelected (): void {
	}

	public async fetchOptions (
		search: string,
		loading: (isLoading: boolean) => void
	): Promise<void> {
		if (this.debounceSearch) {
			clearTimeout(this.debounceSearch)
			this.debounceSearch = null
			loading(false)
		}

		if (search === "") {
			this.searchResults = []
		} else {
			loading(true)
			this.debounceSearch = setTimeout(async () => {
				this.searchResults = [
					{
						itemType: "SEARCH",
						title: "Advanced search for \"" + search + "\"",
						search: search
					},
					...await api.allSearch(search)
				]
				loading(false)
			}, 500)
		}
	}

	public pictureUrl (searchResult: any): string {
		if (searchResult.iconUrl) {
			return searchResult.iconUrl!
		} else {
			return api.pictureUrl(searchResult.id)
		}
	}

	public goToSearchResult (result: any): void {
		if (result.itemType === "RECORD") {
			this.$router.push({ path: `/cases/${result.pageArgument}` })
		} else if (result.itemType === "USER") {
			this.$router.push({ path: `/userprofile/${result.id}` })
		} else if (result.itemType === "SEARCH") {
			this.setSearch(result.search)
			this.$router.push({ path: "/search" })
		}
	}

	public loadedImage (result: any): void {

	}

	public imageUrlError (args: any): void {
		this.pictureError = true
	}

	public theme (newTheme: string): void {
		this.setTheme(newTheme)
	}

	private allowAccess () {
		tenantApi.openSupportAccess(this.supportAccessTime ? this.supportAccessTime : { value: null })
		this.supportAccessOpen = false
	}

	private endAccess () {
		tenantApi.openSupportAccess({ value: null })
		this.supportAccessOpen = false
	}

	private openSupportAccess () {
		tenantApi.tenantAccess().then((supportAccess: TenantSupport | null) => {
			this.supportAccess = supportAccess
			this.supportAccessOpen = true
		})
	}

	private refreshProfilePicture (): void {
		this.pictureGuid = Guid.newGuid()
	}
}
